"use client";

import Link from "next/link";
import { formatConditionLabel } from "@shared/products/product-name";
import { canScheduleTruckloads, getCheckoutMinimumQuantity } from "@shared/products/checkout";
import { resolveCheckoutTruckOptions } from "@shared/products/truck-capacity";
import { useState } from "react";
import { formatCents } from "@/lib/formatting/currency";
import { cn } from "@/lib/utils";
import {
  formatFootprint,
  HEIGHT_LABELS,
  quoteHref,
  type BuilderListing,
  type BuilderSpec,
  type Dock,
} from "@/lib/listings/builder-state";
import { AddToCartButton } from "@/components/cart/add-to-cart-button";
import { Slider } from "@/components/ui/slider";
import { Button, buttonVariants } from "@/components/ui/button";
import { TruckFill } from "@/components/illustrations/truck-fill";
import { DockField } from "./dock-field";

interface LoadPanelProps {
  productTypeSlug: string;
  productName: string;
  unitLabel: string;
  truckload: number;
  spec: BuilderSpec;
  quantity: number;
  quantityStep: number;
  onQuantity: (quantity: number) => void;
  dock: Dock | null;
  onDock: (dock: Dock | null) => void;
  dockPending: boolean;
  onDockPending: (pending: boolean) => void;
  pricingPending: boolean;
  picked: BuilderListing[];
  candidates: BuilderListing[];
}


function specSummary(spec: BuilderSpec, productTypeSlug: string): string {
  const grades = spec.conditions
    .map((condition) => formatConditionLabel(condition, productTypeSlug) ?? condition)
    .join(" or ");
  return [
    grades || "any grade",
    spec.footprint ? formatFootprint(spec.footprint) : "any footprint",
    spec.heightBucket ? HEIGHT_LABELS[spec.heightBucket] : "any height",
    spec.walls !== null ? `${spec.walls}-wall` : null,
    ...Object.values(spec.specs),
  ]
    .filter(Boolean)
    .join(" · ");
}

function priceRange(listings: BuilderListing[]): [number, number] | null {
  const prices = listings
    .map((listing) => listing.buyerUnitPriceCents)
    .filter((cents): cents is number => typeof cents === "number" && cents > 0);
  if (prices.length === 0) return null;
  return [Math.min(...prices), Math.max(...prices)];
}

function rangeLabel(range: [number, number] | null, multiplier = 1): string {
  if (!range) return multiplier === 1 ? "quoted" : "—";
  const [low, high] = range.map((cents) => formatCents(cents * multiplier));
  return high === low ? low : `${low} – ${high}`;
}

function QuantitySlider({
  quantity,
  quantityStep,
  truckload,
  unitLabel,
  onQuantity,
}: Pick<LoadPanelProps, "quantity" | "quantityStep" | "truckload" | "unitLabel" | "onQuantity">) {
  const [draft, setDraft] = useState(quantity);
  return (
    <div className="flex items-center gap-3">
      <Slider
        aria-label={`Number of ${unitLabel}`}
        min={quantityStep}
        max={truckload * 2}
        step={quantityStep}
        value={[draft]}
        onValueChange={([next]) => setDraft(next ?? draft)}
        onValueCommit={([next]) => onQuantity(next ?? draft)}
        className="flex-1"
      />
      <span className="min-w-16 text-right tabular-nums text-lg font-semibold">{draft}</span>
    </div>
  );
}

function QuantityControls({
  quantity,
  quantityStep,
  truckload,
  unitLabel,
  onQuantity,
}: Pick<LoadPanelProps, "quantity" | "quantityStep" | "truckload" | "unitLabel" | "onQuantity">) {
  const halfVan = Math.max(quantityStep, Math.round(truckload / 2 / quantityStep) * quantityStep);
  const presets = [
    ...(halfVan < truckload ? [{ label: "½ van", value: halfVan }] : []),
    { label: `Full van · ${truckload}`, value: truckload },
    { label: "2 vans", value: truckload * 2 },
  ];
  return (
    <>
      <QuantitySlider
        key={quantity}
        quantity={quantity}
        quantityStep={quantityStep}
        truckload={truckload}
        unitLabel={unitLabel}
        onQuantity={onQuantity}
      />
      <div className="flex gap-1.5">
        {presets.map((preset) => (
          <Button
            key={preset.label}
            type="button"
            size="compact"
            variant={quantity === preset.value ? "primary" : "outline"}
            onClick={() => onQuantity(preset.value)}
            aria-pressed={quantity === preset.value}
            className={cn("flex-1", quantity !== preset.value && "bg-chrome-2 text-chrome-muted hover:bg-chrome-3")}
          >
            {preset.label}
          </Button>
        ))}
      </div>
    </>
  );
}

function LoadLines({
  productName,
  quantity,
  range,
  pickedCount,
  dock,
}: {
  productName: string;
  quantity: number;
  range: [number, number] | null;
  pickedCount: number;
  dock: Dock | null;
}) {
  const row = "flex justify-between py-1 text-chrome-muted";
  const value = "tabular-nums font-semibold text-chrome-foreground";
  return (
    <dl className="border-t border-chrome-border pt-3 text-sm">
      <div className={row}>
        <dt>{productName}</dt>
        <dd className={value}>{quantity} × {rangeLabel(range)}</dd>
      </div>
      <div className={row}>
        <dt>Yards picked</dt>
        <dd className={value}>{pickedCount}</dd>
      </div>
      <div className={row}>
        <dt>Freight to {dock ? dock.label : "your dock"}</dt>
        <dd className={value}>quoted by a rep</dd>
      </div>
      <div className="mt-1.5 flex justify-between border-t border-chrome-border pt-2.5 text-chrome-muted">
        <dt>Product subtotal</dt>
        <dd className={cn(value, "text-xl")}>{rangeLabel(range, quantity)}</dd>
      </div>
    </dl>
  );
}

function buyNowBlocker(listing: BuilderListing, quantity: number): string | null {
  const minimum = getCheckoutMinimumQuantity(listing);
  if (quantity < minimum) return `This yard sells ${minimum.toLocaleString()} or more per order`;
  const trucks = resolveCheckoutTruckOptions(listing.truckCapacities, listing.productType?.defaultTruckloadQuantities);
  if (!trucks.length) return "This yard's truck options need a freight quote";
  if (!canScheduleTruckloads(minimum, trucks, quantity)) return "This quantity needs a freight quote";
  return null;
}

function pickedNote(picked: BuilderListing[], candidates: BuilderListing[]): string {
  if (picked.length > 0) {
    return `Pricing against ${picked.map((listing) => listing.cityName).filter(Boolean).join(", ")}.`;
  }
  return candidates.length > 0
    ? "Pick yards from the list to price against real stock."
    : "Nothing lists this spec today.";
}

function buyNowCandidate(picked: BuilderListing[]): BuilderListing | undefined {
  return picked.find(
    (listing) => listing.checkoutEnabled && listing.buyerUnitPriceCents && !listing.listedByViewer,
  );
}

function PanelActions({
  productTypeSlug,
  quantity,
  dock,
  spec,
  picked,
  dockPending,
  pricingPending,
}: Pick<LoadPanelProps, "productTypeSlug" | "quantity" | "dock" | "spec" | "picked" | "dockPending" | "pricingPending">) {
  const buyNow = buyNowCandidate(picked);
  const blocker = buyNow ? buyNowBlocker(buyNow, quantity) : null;
  return (
    <div className="mt-auto flex flex-col gap-2">
        {dockPending ? (
          <Button type="button" variant="primary" size="default" disabled>
            Locating your dock…
          </Button>
        ) : (
          <Link href={quoteHref(productTypeSlug, quantity, dock, spec, picked)} className={buttonVariants({ variant: "primary", size: "default" })}>
            Get quotes for this load
          </Link>
        )}
        {buyNow && picked.length === 1 ? (
          <>
            <AddToCartButton
              productId={buyNow._id}
              quantity={quantity}
              productPublicIdentifier={buyNow.publicIdentifier ?? undefined}
              unitPriceCents={buyNow.buyerUnitPriceCents ?? 0}
              productTitle={buyNow.title}
              productTypeSlug={productTypeSlug}
              label="Buy now from this yard"
              disabled={blocker !== null || pricingPending}
            />
            {blocker ? <small className="text-center text-xs text-chrome-muted">{blocker}</small> : null}
          </>
        ) : null}
        <small className="text-center text-xs text-chrome-muted">
          No account needed · a rep confirms delivered price within one business day
        </small>
      </div>
  );
}

export function LoadPanel(props: LoadPanelProps) {
  const { productTypeSlug, productName, unitLabel, truckload, spec, quantity, dock, dockPending, picked, candidates } = props;
  const range = priceRange(picked.length > 0 ? picked : candidates);
  return (
    <aside
      aria-label="Your load"
      className="shell-chrome flex flex-col gap-4 bg-chrome p-5 text-chrome-foreground lg:order-last lg:sticky lg:top-16 lg:h-[calc(100dvh-4rem)] lg:overflow-auto"
    >
      <div>
        <h2 className="text-overline uppercase text-chrome-muted">Your load</h2>
        <p className="mt-1 text-3xl font-extrabold leading-none tracking-tight">
          {quantity.toLocaleString()} {unitLabel}
        </p>
        <p className="mt-1 text-sm text-chrome-muted">{specSummary(spec, productTypeSlug)}</p>
      </div>
      <TruckFill quantity={quantity} truckload={truckload} unitLabel={unitLabel} />
      <QuantityControls
        quantity={quantity}
        quantityStep={props.quantityStep}
        truckload={truckload}
        unitLabel={unitLabel}
        onQuantity={props.onQuantity}
      />
      <DockField dock={dock} onDock={props.onDock} onPending={props.onDockPending} />
      <LoadLines productName={productName} quantity={quantity} range={range} pickedCount={picked.length} dock={dock} />
      <p className="text-xs text-chrome-muted">{pickedNote(picked, candidates)}</p>
      <PanelActions
        productTypeSlug={productTypeSlug}
        quantity={quantity}
        dock={dock}
        spec={spec}
        picked={picked}
        dockPending={dockPending}
        pricingPending={props.pricingPending}
      />
    </aside>
  );
}
